import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { MailWarning, Download, RefreshCw, ExternalLink } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-CA", { timeZone: "America/Toronto", dateStyle: "medium", timeStyle: "short" }) : "—");
const pct = (v) => (v === null || v === undefined ? "—" : `${v}%`);
const PERIODS = [[3, "3 months"], [6, "6 months"], [12, "12 months"]];

function Tile({ label, value, sub, tone = "slate", testid }) {
  const tones = { slate: "border-slate-200 bg-slate-50 text-slate-900", red: "border-red-200 bg-red-50 text-red-800", amber: "border-amber-200 bg-amber-50 text-amber-800" };
  return (
    <div className={`rounded-md border px-2 py-2 text-center ${tones[tone]}`} data-testid={testid}>
      <div className="text-lg font-bold tabular-nums leading-tight">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 leading-tight">{label}</div>
      {sub && <div className="text-[10px] text-slate-500 leading-tight">{sub}</div>}
    </div>
  );
}

function RateTable({ rows, firstCol, testid }) {
  return (
    <div className="rounded-md border border-slate-200 overflow-x-auto">
      <table className="w-full text-xs" data-testid={testid}>
        <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
          <tr>
            <th className="px-3 py-2 text-left">{firstCol}</th>
            <th className="px-3 py-2 text-right">Sent</th>
            <th className="px-3 py-2 text-right">Opened</th>
            <th className="px-3 py-2 text-right">Clicked</th>
            <th className="px-3 py-2 text-right">Bounced</th>
            <th className="px-3 py-2 text-right">Soft</th>
            <th className="px-3 py-2 text-right">Unsub.</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={7} className="px-3 py-4 text-center text-slate-400">No e-mails in this period.</td></tr>}
          {rows.map((r) => (
            <tr key={r.type} className="border-t" data-testid={`${testid}-row-${r.type}`}>
              <td className="px-3 py-1.5 whitespace-nowrap font-medium">{r.label}</td>
              <td className="px-3 py-1.5 text-right tabular-nums">{r.sent}</td>
              <td className="px-3 py-1.5 text-right tabular-nums">{r.opened} <span className="text-slate-400">· {pct(r.open_rate)}</span></td>
              <td className="px-3 py-1.5 text-right tabular-nums">{r.clicked} <span className="text-slate-400">· {pct(r.click_rate)}</span></td>
              <td className={`px-3 py-1.5 text-right tabular-nums ${r.bounced ? "text-red-700 font-semibold" : ""}`}>{r.bounced} <span className="text-slate-400 font-normal">· {pct(r.bounce_rate)}</span></td>
              <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">{r.soft_bounces}</td>
              <td className="px-3 py-1.5 text-right tabular-nums">{r.unsubscribed}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Settings → Photo / review requests: deliverability of EVERY customer e-mail LPC sends through Gmail.
export function EmailDeliverabilityCard() {
  const qc = useQueryClient();
  const [months, setMonths] = useState(6);
  const [byMonth, setByMonth] = useState(false);
  const { data, isLoading, error } = useQuery({ queryKey: ["email-report", months], queryFn: async () => (await api.get(`/email-report?months=${months}`)).data });
  const scan = useMutation({
    mutationFn: async () => (await api.post("/email-bounces/scan")).data,
    onSuccess: (s) => { qc.invalidateQueries({ queryKey: ["email-report"] }); toast.success(`Inbox scanned · ${s.fetched} notices · ${s.new} new (${s.hard} hard, ${s.soft} soft)`); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Scan failed"),
  });
  const exportCsv = async () => {
    try {
      const resp = await api.get(`/email-report.csv?months=${months}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `lpc-email-report-${months}m.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e?.response?.data?.detail || "Export failed"); }
  };
  const t = data?.totals || {};
  const bs = data?.bounce_scan || {};
  return (
    <div className="mt-6 rounded-lg border border-slate-200 bg-slate-50/60 p-4" data-testid="email-deliverability-card">
      <div className="flex items-center gap-2 flex-wrap">
        <MailWarning size={18} className="text-slate-700" />
        <h3 className="font-semibold">E-mail deliverability · all customer e-mails</h3>
        <span className="text-xs text-slate-500">sent through the connected Gmail account</span>
        <span className="ml-auto flex items-center gap-1">
          {PERIODS.map(([m, l]) => (
            <button key={m} type="button" onClick={() => setMonths(m)} className={`rounded-full px-3 py-1 text-xs border ${months === m ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"}`} data-testid={`email-report-period-${m}`}>{l}</button>
          ))}
        </span>
      </div>
      {error && <div className="mt-3 text-sm text-rose-700" data-testid="email-report-error">{error?.response?.data?.detail || "Could not load the report"}</div>}
      {isLoading && <div className="mt-3 text-sm text-slate-400" data-testid="email-report-loading">Loading…</div>}
      {data && (
        <>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="email-report-totals">
            <Tile label="Sent" value={t.sent ?? 0} sub={`${data.period.from} → ${data.period.to}`} testid="email-report-sent" />
            <Tile label="Open rate" value={pct(t.open_rate)} sub={`${t.opened ?? 0} opened`} testid="email-report-open-rate" />
            <Tile label="Click rate" value={pct(t.click_rate)} sub={`${t.clicked ?? 0} clicked`} testid="email-report-click-rate" />
            <Tile label="Bounce rate" value={pct(t.bounce_rate)} sub={`${t.bounced ?? 0} hard · ${t.soft_bounces ?? 0} soft${t.unmatched_hard_bounces ? ` · +${t.unmatched_hard_bounces} not tied to a send` : ""}`} tone={t.bounced || t.unmatched_hard_bounces ? "red" : "slate"} testid="email-report-bounce-rate" />
            <Tile label="Unsubscribed" value={t.unsubscribed ?? 0} sub="review e-mails only" testid="email-report-unsubscribed" />
            <Tile label="Complaints" value="n/a" sub="see Postmaster Tools" tone="amber" testid="email-report-complaints" />
          </div>
          <div className="mt-3 flex items-center gap-2 flex-wrap text-xs">
            <button type="button" onClick={() => setByMonth(false)} className={`underline-offset-2 ${!byMonth ? "font-semibold text-slate-900 underline" : "text-slate-500"}`} data-testid="email-report-view-type">By e-mail type</button>
            <span className="text-slate-300">|</span>
            <button type="button" onClick={() => setByMonth(true)} className={`underline-offset-2 ${byMonth ? "font-semibold text-slate-900 underline" : "text-slate-500"}`} data-testid="email-report-view-month">By month</button>
            <span className="ml-auto flex items-center gap-2">
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={exportCsv} data-testid="email-report-export-csv"><Download size={12} className="mr-1" />CSV</Button>
              <Button size="sm" variant="outline" className="h-7 text-xs" disabled={scan.isPending || !bs.inbox_access} onClick={() => scan.mutate()} title={bs.inbox_access ? `Reads delivery-failure notices from the last ${bs.lookback_days} days (also runs every ${bs.every_minutes} min)` : "Connect Gmail with inbox access first"} data-testid="email-bounces-scan-now"><RefreshCw size={12} className={`mr-1 ${scan.isPending ? "animate-spin" : ""}`} />{scan.isPending ? "Scanning…" : "Scan inbox for bounces"}</Button>
            </span>
          </div>
          <div className="mt-2">
            {byMonth ? <RateTable rows={data.by_month} firstCol="Month" testid="email-report-by-month" /> : <RateTable rows={data.by_type} firstCol="E-mail type" testid="email-report-by-type" />}
          </div>
          <div className="mt-3 grid gap-3 lg:grid-cols-[minmax(0,1fr)_280px]">
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-widest font-mono text-slate-500 mb-1">Recent bounces</div>
              <div className="rounded-md border border-slate-200 bg-white overflow-x-auto">
                <table className="w-full text-xs" data-testid="email-bounces-table">
                  <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                    <tr><th className="px-3 py-2 text-left">When</th><th className="px-3 py-2 text-left">Address</th><th className="px-3 py-2 text-left">Order</th><th className="px-3 py-2 text-left">E-mail</th><th className="px-3 py-2 text-left">Type</th><th className="px-3 py-2 text-left">Reason</th></tr>
                  </thead>
                  <tbody>
                    {(data.bounces || []).length === 0 && <tr><td colSpan={6} className="px-3 py-4 text-center text-slate-400" data-testid="email-bounces-empty">No bounces recorded{bs.last_scan_at ? ` · last scan ${fmt(bs.last_scan_at)}` : " · inbox not scanned yet"}.</td></tr>}
                    {(data.bounces || []).map((b) => (
                      <tr key={b.id} className="border-t" data-testid={`email-bounce-row-${b.id}`}>
                        <td className="px-3 py-1.5 whitespace-nowrap">{fmt(b.received_at)}</td>
                        <td className="px-3 py-1.5 font-mono">{b.recipient}</td>
                        <td className="px-3 py-1.5 whitespace-nowrap">{b.order_id ? <a href={`/order/${b.order_id}`} className="underline text-sky-700">{b.po_number || "order"}</a> : <span className="text-slate-400">not matched</span>}</td>
                        <td className="px-3 py-1.5 max-w-[220px] truncate" title={b.original_subject || ""}>{b.original_subject || "—"}</td>
                        <td className="px-3 py-1.5 whitespace-nowrap">{b.hard ? <span className="rounded bg-red-100 text-red-800 px-1.5 py-0.5 font-semibold">hard</span> : <span className="rounded bg-amber-100 text-amber-800 px-1.5 py-0.5">soft</span>}{b.template_key ? <span className="text-slate-500"> · {b.template_key}</span> : null}</td>
                        <td className="px-3 py-1.5 text-slate-600 max-w-[260px] truncate" title={b.reason || ""}>{b.reason || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="text-xs text-slate-600 space-y-2">
              <div className="rounded-md border border-slate-200 bg-white p-3" data-testid="email-bounce-scan-status">
                <div className="font-semibold text-slate-800">Bounce scan</div>
                {!bs.gmail_connected && <div className="text-rose-700">Gmail not connected.</div>}
                {bs.gmail_connected && !bs.inbox_access && <div className="text-rose-700">Connected account has no inbox-read permission — reconnect Gmail in the AI e-mail assistant settings.</div>}
                {bs.inbox_access && <div>Every {bs.every_minutes} min · last {fmt(bs.last_scan_at)}{bs.last_scan?.error ? <span className="text-rose-700"> · {bs.last_scan.error}</span> : ""}</div>}
                <div className="mt-1 text-slate-500">A hard bounce flags the customer (<span className="text-red-700 font-semibold">✕ e-mail bounced</span>) and pauses automatic e-mails to that address; manual sends still work. Correcting the address in Order Info lifts the hold.</div>
              </div>
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3" data-testid="email-complaints-note">
                <div className="font-semibold text-amber-900">Spam complaints</div>
                <div className="text-amber-900/80">{data.complaints?.note}</div>
                <a href={data.complaints?.postmaster_url} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 underline text-amber-900" data-testid="email-postmaster-link">Open Google Postmaster Tools <ExternalLink size={11} /></a>
              </div>
              <ul className="list-disc pl-4 text-slate-500 space-y-0.5">
                {(data.notes || []).map((n) => <li key={n}>{n}</li>)}
              </ul>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
